/**
 * Sep 04, 2026 · SearchablePicker — universal command-palette picker.
 *
 * Drop-in replacement for a shadcn `<Select>` when the option list has
 * more than ~15 entries AND you want operators to type-and-narrow by
 * BOTH the code (`RM-001`, `INV-26-27-00002`) AND the name /
 * description (`Yarn 70D · Nylon`). Uses shadcn `Command` inside a
 * `Popover` so the UX is identical everywhere it renders — one place
 * to fix if the interaction ever needs to change.
 *
 * Props
 * -----
 *  options      Array<{ value, code, name, meta? }>. Rendered as
 *               `[code] name · meta`. `meta` is optional secondary
 *               copy (category, UOM, etc.).
 *  value        Currently-selected `value`. `""` when unset.
 *  onChange     `(value) => void`. Fires with the picked option's `value`.
 *  placeholder  Trigger button copy when no value is picked.
 *  emptyLabel   Copy shown when no options match the search.
 *  testid       Prefix for `data-testid` on trigger + list rows.
 *  disabled     Blocks opening the popover.
 *  className    Optional extra classes on the trigger.
 *  headerHint   Optional small text under the search input.
 */
import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { CaretUpDownIcon, CheckIcon, XCircleIcon } from "@phosphor-icons/react";

export default function SearchablePicker({
  options = [],
  value = "",
  onChange,
  placeholder = "Pick…",
  emptyLabel = "No matches — refine your search.",
  testid = "picker",
  disabled = false,
  className = "",
  headerHint = "",
  clearable = false,
  footer = null,
  groupHeading = "",
  renderOption = null,
  // Feb 2026 · Optional override for the popover width. Long Product
  // Codes on the WO picker need ~480–720px so the full code fits
  // without truncation; every other picker keeps the default 320px.
  popoverClassName = "",
  // Feb 2026 · Optional per-row padding override. Set to `py-2` for
  // touch-friendly WO rows; defaults empty so the standard pickers
  // stay compact.
  itemClassName = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // O(1) reverse-lookup so the trigger label stays fresh when parent
  // hydrates async data after mount.
  const byValue = useMemo(() => {
    const m = new Map();
    for (const o of options) m.set(String(o.value), o);
    return m;
  }, [options]);
  const active = byValue.get(String(value));

  // Filter locally — Command's built-in filter only matches the visible
  // string, so we join `code + name + meta` into one haystack per row.
  const filtered = useMemo(() => {
    const n = query.trim().toLowerCase();
    if (!n) return options;
    return options.filter(o => {
      const hay = `${o.code || ""} ${o.name || ""} ${o.meta || ""}`.toLowerCase();
      return hay.includes(n);
    });
  }, [options, query]);

  const commit = (val) => {
    onChange?.(val);
    setOpen(false);
    setQuery("");
  };

  const triggerCls =
    "flex h-9 w-full items-center justify-between rounded-sm border border-[var(--border)] bg-[var(--bg-0)] px-3 text-sm text-left transition-colors " +
    (disabled ? "opacity-50 cursor-not-allowed " : "hover:border-[var(--accent)] ") +
    className;

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testid}-trigger`}
          className={triggerCls}
          disabled={disabled}
        >
          <span className="truncate flex items-center gap-2 min-w-0">
            {active ? (
              <>
                <span className="font-mono text-xs text-[var(--accent)] flex-shrink-0">{active.code}</span>
                {active.name && <span className="text-zinc-300 truncate">{active.name}</span>}
                {active.meta && <span className="text-[10px] text-zinc-500 flex-shrink-0">· {active.meta}</span>}
              </>
            ) : (
              <span className="text-zinc-500">{placeholder}</span>
            )}
          </span>
          <span className="flex items-center gap-1 flex-shrink-0">
            {clearable && value && !disabled && (
              <XCircleIcon
                size={14}
                className="text-zinc-500 hover:text-red-400"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); commit(""); }}
                data-testid={`${testid}-clear`}
              />
            )}
            <CaretUpDownIcon size={14} className="text-zinc-500" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={`p-0 bg-[var(--bg-1)] border-[var(--border)] w-[--radix-popover-trigger-width] min-w-[320px] ${popoverClassName}`}
        align="start"
        /* Mar 2026 · Scroll Fix — when the picker mounts inside a
           Dialog (New Loom Entry modal), Radix's Popover portal can
           lose wheel/touch events to the Dialog's focus trap. Stop
           propagation on wheel + touch at the popover root so the
           list scrolls smoothly regardless of parent overlays. */
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onPointerDownOutside={(e) => {
          // Allow closing when clicking OUTSIDE the popover but keep
          // wheel/scroll events inside from bubbling to the Dialog.
          if (e.target && e.target.closest && e.target.closest('[data-radix-popper-content-wrapper]')) {
            e.preventDefault();
          }
        }}
      >
        <Command className="bg-[var(--bg-1)]" shouldFilter={false}>
          <CommandInput
            data-testid={`${testid}-search`}
            placeholder="Type code or name…"
            value={query}
            onValueChange={setQuery}
            className="h-9"
          />
          {headerHint && (
            <div className="px-3 py-1.5 text-[10px] text-zinc-500 font-mono border-b border-[var(--border)]">{headerHint}</div>
          )}
          {/* Mar 2026 · Explicit scroll surface — belt-and-suspenders
              on top of shadcn's default `max-h-[300px] overflow-y-auto`.
              `touch-action: pan-y` unlocks native mobile scroll;
              `overscroll-contain` blocks page-scroll leak when the
              list hits its ends. */}
          <CommandList
            className="max-h-[300px] overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
          >
            <CommandEmpty className="py-6 text-center text-[11px] text-zinc-500">{emptyLabel}</CommandEmpty>
            <CommandGroup heading={groupHeading || undefined}>
              {filtered.map((o) => {
                const isActive = String(o.value) === String(value);
                if (renderOption) {
                  return (
                    <CommandItem
                      key={o.value}
                      data-testid={`${testid}-opt-${o.value}`}
                      value={String(o.value)}
                      onSelect={() => commit(o.value)}
                      className={`flex items-start gap-2 text-sm ${itemClassName}`}
                    >
                      <CheckIcon size={12} weight="bold" className={`mt-1 flex-shrink-0 ${isActive ? "text-emerald-400" : "text-transparent"}`} />
                      {renderOption(o, { isActive })}
                    </CommandItem>
                  );
                }
                return (
                  <CommandItem
                    key={o.value}
                    data-testid={`${testid}-opt-${o.value}`}
                    value={String(o.value)}
                    onSelect={() => commit(o.value)}
                    className={`flex items-center gap-2 text-sm ${itemClassName}`}
                  >
                    <CheckIcon size={12} weight="bold" className={isActive ? "text-emerald-400" : "text-transparent"} />
                    <span className="font-mono text-xs text-[var(--accent)]">{o.code}</span>
                    {o.name && <span className="text-zinc-200 truncate">{o.name}</span>}
                    {o.meta && <span className="text-[10px] text-zinc-500 ml-auto">{o.meta}</span>}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {footer && (
              <div className="p-1 border-t border-[var(--border)]" data-testid={`${testid}-footer`}>
                {typeof footer === "function" ? footer({ query, close: () => setOpen(false) }) : footer}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
